import React, {MouseEvent, useEffect, useMemo, useState} from "react";
import Grid from "@mui/material/Grid";
import BasicTable from "../../components/tables/BasicTable";
import {ColumnDef, ColumnFiltersState} from "@tanstack/react-table";
import {useAppDispatch, useAppSelector} from "../../reducers/hooks";
import {DocumentDownload, Edit, Trash} from "iconsax-react";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AlertExampleElementDelete from "../../sections/modals/exampleElementModal/AlertExampleElementDelete";
import ElementModalGeneric from "sections/modals/exampleElementModal/ElementModalGeneric";
import {deleteElement, getAllElement, ShedElement} from "reducers/Shed/ShedReducer";
import TablePagination from "@mui/material/TablePagination";
import {openSnackbar, SnackbarState} from "reducers/snackbar/snackbarReducer";
import {FormattedMessage, useIntl} from "react-intl";
import {ShedForm} from "../../sections/forms/Shed/ShedForm";
import ViewShedModal from "../../components/tables/modal-historical/ViewShedModal";
import CustomLoader from "../../components/CustomLoader";
import {Document, Page, pdf, Text, View} from '@react-pdf/renderer';
import dayjs from "dayjs";

export default function Shed() {

    const dispatch = useAppDispatch();
    const intl = useIntl()

    const [open, setOpen] = useState<boolean>(false);
    const {elements, currentPage, totalRows, sort, loading} = useAppSelector(state => {
        return state.shedElement;
    });
    const [selectedElement, setSelectedElement] = useState<ShedElement | null>(null);
    const [selectedDeleteElement, setSelectedDeleteElement] = useState<any>();
    const [page, setPage] = useState(0);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState<boolean>(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
    const [selectedShed, setSelectedShed] = useState<string | undefined>(undefined);

    const [filters, setFilters] = useState<ColumnFiltersState>([])

    const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };


    const handleModalOpen = (isOpen: boolean) => {
        setOpen(isOpen);
        if (!isOpen) setSelectedElement(null);
    };


    const columnFiltersState = (values: ColumnFiltersState) => {
        setPage(0)
        setFilters(values)

    };
    const columns = useMemo<ColumnDef<ShedElement>[]>(() => [
        {
            header: intl.formatMessage({id: "Name"}),
            accessorKey: 'name',
        },
        {
            header: () => <FormattedMessage id="Actions" defaultMessage="Actions"/>,
            accessorKey: 'Actions',
            enableColumnFilter: false,
            meta: {
                className: 'cell-right'
            },
            disableSortBy: true,
            cell: ({row}) => {
                const shed = row.original;

                const handlePrint = async () => {
                    try {
                        const blob = await pdf(
                            <Document>
                                <Page
                                    size="A4"
                                    style={{
                                        padding: 30,
                                        fontFamily: 'Helvetica',
                                        fontSize: 12,
                                        lineHeight: 1.6,
                                        color: '#000',
                                    }}
                                >
                                    {/* Titolo */}
                                    <Text style={{fontSize: 16, fontWeight: 'bold', marginBottom: 10}}>
                                        Nome Capannone: <Text style={{fontWeight: 'normal'}}>{shed.name || 'N/A'}</Text>
                                    </Text>

                                    <Text style={{fontSize: 14, fontWeight: 'bold', marginBottom: 8}}>
                                        Microchip dei cani nel Capannone: <Text
                                        style={{fontWeight: 'normal'}}>{shed.microchipeCodeForShed?.length || 'N/A'}</Text>
                                    </Text>

                                    <Text style={{fontSize: 12, color: '#000', marginBottom: 16}}>
                                        Report generato il: {dayjs().format("DD/MM/YYYY")}
                                    </Text>

                                    {/* Microchip container */}
                                    <View
                                        style={{
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            justifyContent: 'flex-start',
                                            gap: 8, // anche se ignorato, è ok lasciarlo per leggibilità
                                            marginLeft: "5%"

                                        }}
                                    >
                                        {Array.isArray(shed.microchipeCodeForShed) && shed.microchipeCodeForShed.length > 0 ? (
                                            shed.microchipeCodeForShed.map((micro, index) => (
                                                <View
                                                    key={index}
                                                    wrap={false}
                                                    style={{
                                                        width: '30%',
                                                        padding: 3,
                                                    }}
                                                >
                                                    <View
                                                        style={{
                                                            backgroundColor: '#e0e0e0',
                                                            border: '1pt solid #000',
                                                            borderRadius: 10,
                                                            paddingVertical: 3,
                                                            paddingHorizontal: 5,
                                                            textAlign: 'center',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Text style={{fontSize: 14, textAlign: "center"}}>{micro}</Text>
                                                    </View>
                                                </View>

                                            ))
                                        ) : (
                                            <Text style={{fontSize: 12}}>
                                                Nessun cane in questo capannone
                                            </Text>
                                        )}
                                    </View>
                                </Page>
                            </Document>
                        ).toBlob();

                        const fileName = `${shed.name || 'shed'}_report.pdf`;

                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    } catch (error) {
                        console.error('Errore nella generazione del PDF:', error);
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: intl.formatMessage({
                                    id: 'Errore nella generazione del PDF',
                                }),
                                variant: 'alert',
                                alert: {
                                    color: 'error',
                                },
                            } as SnackbarState)
                        );
                    }
                };


                return (
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
                        <Tooltip title={<FormattedMessage id="edit" defaultMessage="edit"/>}>
                            <IconButton
                                color="primary"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    setSelectedElement(row.original);
                                    setOpen(true);
                                }}
                            >
                                <Edit/>
                            </IconButton>
                        </Tooltip>
                        {/* <Tooltip title={<FormattedMessage id="View" defaultMessage="View" />}>
                            <IconButton
                                color="default"
                                onClick={(e) => {

                                    e.stopPropagation();
                                    setSelectedShed(row.original._id || undefined);
                                    setViewModalOpen(true);
                                }}
                            >
                                <Eye variant="Bold"/>
                            </IconButton>
                        </Tooltip>*/}
                        <Tooltip title={<FormattedMessage id="Delete" defaultMessage="Delete"/>}>
                            <IconButton
                                color="error"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    setSelectedDeleteElement(row.original);
                                    setDeleteAlertOpen(true);

                                }}
                            >
                                <Trash/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={<FormattedMessage id="Download" defaultMessage="Download"/>}>
                            <IconButton
                                color="default"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrint();
                                }}
                            >
                                <DocumentDownload/>
                            </IconButton>
                        </Tooltip>
                    </Stack>

                );
            },
        }
    ], [intl]); // Added 'intl' and other dependencies for useMemo

    useEffect(() => {
        dispatch(getAllElement({
            pageableDto: {
                page,
                size: rowsPerPage,
                sort: ['dateCreated', 'asc']
            },
            filters
        }));
    }, [page, rowsPerPage, JSON.stringify(filters)]);

    const onDeleteSuccess = () => {
        dispatch(getAllElement({pageableDto: {page: currentPage, size: rowsPerPage, sort}, filters: filters}));
        openSnackbar(
            {
                open: true,
                message: 'Element deleted successfully',
                variant: 'alert',
                alert: {
                    color: 'success'
                }
            } as SnackbarState
        )
    }

    const handleDelete = (id: string) => dispatch(deleteElement({id, callback: onDeleteSuccess}));

    const refreshData = () => {
        dispatch(getAllElement({
            pageableDto: {
                page: page,
                size: rowsPerPage,
                sort: ['dateCreated', 'desc']
            },
            filters: filters
        }));
        openSnackbar({
            open: true,
            message: intl.formatMessage({id: 'Shed added complete'}),
            variant: 'alert',
            alert: {
                color: 'success'
            }
        } as SnackbarState);
    };

    return (
        <>
            {/* {loading && (
                <Backdrop
                    sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                    open={true}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
            )}*/}

            <CustomLoader open={loading} message="Caricamento in corso..."/>

            <Grid container>
                <Grid item xs={12}>
                    <BasicTable
                        columns={columns}
                        data={elements} // Passa i dati filtrati alla tabella
                        title={intl.formatMessage({id: "ShedTitle", defaultMessage: "Shed"})}
                        striped
                        modalToggler={() => handleModalOpen(!open)}
                        onFilterChange={columnFiltersState}
                        manualFiltering={true}
                    />

                    <ViewShedModal
                        open={viewModalOpen}
                        onClose={() => setViewModalOpen(false)}
                        shedData={selectedShed}
                    />

                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                    <ElementModalGeneric
                        open={open}
                        setOpen={handleModalOpen}
                        element={selectedElement}
                        FormComponent={ShedForm}
                        onSuccess={refreshData}
                        pageableData={{
                            page,
                            rowsPerPage,
                            filters
                        }}
                    />
                    {selectedDeleteElement && (
                        <AlertExampleElementDelete
                            id={selectedDeleteElement?._id}
                            title={selectedDeleteElement?.name || ""}
                            open={deleteAlertOpen}
                            handleClose={() => setDeleteAlertOpen(false)}
                            onDelete={(_id) => handleDelete(_id)}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
}
